import React from 'react';
import { Avatar } from '@cubemeet/ui';
import StepperInputWeb from './StepperInputWeb';

type SplitMode = 'equal' | 'exact' | 'percentage' | 'shares';

interface Member { id: string; name: string; avatar?: string }

interface Props {
  members: Member[];
  splitEnabled: boolean;
  splitMode: SplitMode;
  selectedIds: string[];
  splitInputs: Record<string, string>;
  percentageValues: Record<string, number>;
  shareValues: Record<string, number>;
  onToggle: (id: string) => void;
  onToggleSplit?: (enabled: boolean) => void;
  onModeChange: (mode: SplitMode) => void;
  onInputChange: (id: string, value: string) => void;
  onValueChange: (id: string, value: number) => void;
}

const PrivateSplitWeb: React.FC<Props> = ({ members, splitEnabled, splitMode, selectedIds, splitInputs, percentageValues, shareValues, onToggle, onToggleSplit, onModeChange, onInputChange, onValueChange }) => {
  const handleToggleSplit = (v?: boolean) => {
    if (typeof onToggleSplit === 'function') onToggleSplit(typeof v === 'boolean' ? v : !splitEnabled);
  };

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.title}>Private split</div>
          <div style={styles.subtitle}>Private expenses happen between members and stay outside the team ledger.</div>
        </div>
        <div>
          <button
            aria-pressed={splitEnabled}
            onClick={() => handleToggleSplit()}
            style={{ ...styles.switchButton, ...(splitEnabled ? styles.switchButtonOn : {}) }}
            aria-label={splitEnabled ? 'Disable private split' : 'Enable private split'}
          >
            <span style={{ ...styles.switchKnob, ...(splitEnabled ? styles.switchKnobOn : {}) }} />
          </button>
        </div>
      </div>

      {splitEnabled && (
        <>
          <div style={styles.modeContainer}>
            <div style={styles.modeRow}>
              {[{ label: '=', mode: 'equal' }, { label: '1.23', mode: 'exact' }, { label: '%', mode: 'percentage' }, { label: '|||', mode: 'shares' }].map(m => (
                <button key={m.mode} onClick={() => onModeChange(m.mode as SplitMode)} style={{ ...styles.modeBtn, ...(splitMode === m.mode ? styles.modeBtnActive : {}) }}>{m.label}</button>
              ))}
            </div>
          </div>

          <div>
            {members.map(member => {
              const selected = selectedIds.includes(member.id);
              return (
                <div key={member.id} style={{ ...styles.memberRow, ...(selected ? {} : styles.dimmed) }}>
                  <div style={styles.memberInfo} onClick={() => onToggle(member.id)}>
                    <div style={styles.avatarWrap}>
                      <Avatar size="large" avatarUrl={member.avatar ?? null} displayName={member.name} />
                      {selected && <div style={styles.badge}>✓</div>}
                    </div>
                    <div style={styles.memberNameColumn}>
                      <div style={styles.memberName}>{member.name}</div>
                      {(splitMode === 'percentage' || splitMode === 'shares') && (
                        <div style={styles.memberAmountSubtitle}>CNY {splitInputs[member.id] ?? '0.00'}</div>
                      )}
                    </div>
                  </div>
                  <div style={styles.memberControl}>
                    {splitMode === 'percentage' ? (
                      <StepperInputWeb value={percentageValues[member.id] ?? 0} onValueChange={(v) => onValueChange(member.id, v)} step={0.1} minimum={0} maximum={100} label="%" disabled={!selected} />
                    ) : splitMode === 'shares' ? (
                      <StepperInputWeb value={shareValues[member.id] ?? 1.0} onValueChange={(v) => onValueChange(member.id, v)} step={0.1} minimum={0} label="份" disabled={!selected} />
                    ) : (
                      <div style={styles.currencyRow}>
                        <div style={styles.currencyLabel}>CNY</div>
                        <input value={splitInputs[member.id] ?? ''} onChange={(e) => onInputChange(member.id, e.target.value)} style={styles.underlineInput} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: { background: '#fff', padding: 20, borderRadius: 12, marginBottom: 16, boxShadow: '0 2px 10px rgba(16,24,40,0.04)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 8 },
  subtitle: { color: '#6b7280', marginBottom: 12 },
  modeContainer: { display: 'flex', justifyContent: 'center', marginBottom: 12, padding: '8px 12px', borderRadius: 10, background: 'transparent' },
  modeRow: { display: 'flex', gap: 10, alignItems: 'center' },
  modeBtn: { padding: '10px 14px', borderRadius: 8, border: '1px solid #f1f1f1', background: '#fff', cursor: 'pointer', minWidth: 64, textAlign: 'center', boxShadow: 'none' },
  modeBtnActive: { background: '#fb6d3a', color: '#fff', borderColor: '#fb6d3a', boxShadow: 'none' },
  memberRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f0f0f0' },
  dimmed: { opacity: 0.5 },
  memberInfo: { display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' },
  avatarWrap: { position: 'relative', width: 56, height: 56, flex: '0 0 56px' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 28 },
  avatar: { width: 56, height: 56, borderRadius: 28, background: '#eaf6f3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#1f2937' },
  badge: { position: 'absolute', top: -6, left: 38, width: 26, height: 26, borderRadius: 13, background: '#fb6d3a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, boxShadow: '0 2px 6px rgba(0,0,0,0.12)' },
  memberName: { marginLeft: 12, fontWeight: 700, fontSize: 16 },
  memberNameColumn: { marginLeft: 12, display: 'flex', flexDirection: 'column' },
  memberAmountSubtitle: { color: '#6b7280', marginTop: 4, fontSize: 13 },
  memberControl: { minWidth: 160, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
  smallInput: { width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid #e5e7eb' },
  currencyRow: { display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' },
  currencyLabel: { color: '#111827', fontWeight: 600, marginRight: 12 },
  underlineInput: { width: 140, padding: '6px 4px', border: 'none', borderBottom: '2px solid #111827', outline: 'none', textAlign: 'right' },
  amountInput: { width: 120, padding: '6px 8px', borderRadius: 6, border: '1px solid #e5e7eb', textAlign: 'right' },
  switchButton: { width: 44, height: 26, borderRadius: 16, padding: 3, border: 'none', background: '#e6e9ee', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start' },
  switchButtonOn: { background: '#fb6d3a', justifyContent: 'flex-end' },
  switchKnob: { width: 20, height: 20, borderRadius: 10, background: '#ffffff', boxShadow: '0 1px 2px rgba(0,0,0,0.12)', transition: 'transform 160ms ease' },
  switchKnobOn: { transform: 'translateX(0)' },
};

export default PrivateSplitWeb;
